<script setup>
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { isFooterRow } from '../document/ops';
import { themeColor, paddingCss, isHidden } from './canvasStyles';
import CanvasColumn from './CanvasColumn.vue';
import SelectionToolbar from './SelectionToolbar.vue';
import ColumnDivider from './ColumnDivider.vue';

const props = defineProps({
  row: { type: Object, required: true },
  width: { type: Number, required: true },
});
const ed = inject('emailEditor');
const { t } = useI18n();

const theme = computed(() => ed.template.value?.theme || {});
const mobile = computed(() => ed.device.value === 'mobile');
const st = computed(() => props.row.style || {});
const stacked = computed(
  () => mobile.value && st.value.stack_on_mobile !== false
);
const selected = computed(() => ed.selectedId.value === props.row.id);
const highlighted = computed(
  () => !!ed.highlightIds?.value?.includes(props.row.id)
);
const outlineClass = computed(() => {
  if (selected.value) return 'outline outline-n-brand';
  if (highlighted.value) return 'outline outline-n-amber-9';
  return 'hover:outline hover:outline-n-brand/40';
});
const footer = computed(() => isFooterRow(props.row));
const hidden = computed(() => isHidden(st.value, ed.device.value));
const contentPx = computed(
  () =>
    props.width - (st.value.padding?.[1] || 0) - (st.value.padding?.[3] || 0)
);

const rowStyle = computed(() => ({
  backgroundColor: themeColor(st.value.background, theme.value) || undefined,
  backgroundImage: st.value.background_image?.url
    ? `url('${st.value.background_image.url}')`
    : undefined,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: paddingCss(st.value.padding, { mobile: mobile.value, row: true }),
  borderRadius: st.value.radius ? `${st.value.radius}px` : undefined,
}));
const label = computed(() =>
  footer.value
    ? t('EMAIL_TEMPLATES.EDITOR.NODE.footer')
    : `${t('EMAIL_TEMPLATES.EDITOR.NODE.row')} · ${props.row.columns.length}`
);
const columnDirection = computed(() => {
  if (!stacked.value) return 'flex-row';
  return st.value.reverse_on_mobile ? 'flex-col-reverse' : 'flex-col';
});
const hiddenLabel = computed(() =>
  t(
    st.value.hide_on === 'mobile'
      ? 'EMAIL_TEMPLATES.EDITOR.CANVAS.HIDDEN_MOBILE'
      : 'EMAIL_TEMPLATES.EDITOR.CANVAS.HIDDEN_DESKTOP'
  )
);
</script>

<template>
  <div
    :data-row="row.id"
    class="group/row relative outline-2 -outline-offset-2"
    :data-highlight="highlighted || undefined"
    :class="[outlineClass, hidden ? 'opacity-40' : '']"
    @click.stop="ed.select(row.id)"
  >
    <span
      v-if="!footer"
      data-row-handle
      class="absolute left-1 top-6 z-10 hidden cursor-grab rounded bg-n-brand p-1 text-white group-hover/row:block"
      :class="{ '!block': selected }"
    >
      <span class="i-lucide-grip-vertical block size-4" />
    </span>
    <span
      class="pointer-events-none absolute left-0 top-0 z-10 hidden rounded-br bg-n-brand px-1.5 py-0.5 text-[10px] font-medium text-white group-hover/row:block"
      :class="{ '!block': selected }"
      >{{ label }}</span
    >
    <span
      v-if="hidden"
      class="absolute bottom-1 right-1 z-10 rounded bg-n-slate-12 px-1.5 py-0.5 text-[10px] text-white"
    >
      {{ hiddenLabel }}
    </span>
    <SelectionToolbar
      v-if="selected"
      :node-id="row.id"
      kind="row"
      :locked="footer"
      class="absolute right-1 top-1 z-20"
    />
    <div :style="rowStyle">
      <div data-columns class="flex" :class="columnDirection">
        <template v-for="(col, i) in row.columns" :key="col.id">
          <CanvasColumn :column="col" :row="row" :stacked="stacked" />
          <ColumnDivider
            v-if="selected && !stacked && i < row.columns.length - 1"
            :widths="row.columns.map(c => c.width)"
            :index="i"
            :content-px="contentPx"
            @change="widths => ed.setWidths(row.id, widths)"
          />
        </template>
      </div>
    </div>
  </div>
</template>
